//{{-- --------------------------------------------------------------------------------------------- --}}
//{{-- Counts words in files                                                                         --}}
//{{-- For the moment, contains functions for OOXML files and IDML:                                  --}}
//{{-- - asynchCountWordsInDocx                                                                      --}}
//{{-- - asynchCountWordsInPptx                                                                      --}}
//{{-- - asynchCountWordsInXlsx                                                                      --}}
//{{-- - asynchCountWordsInIdml                                                                      --}}
//{{--                                                                                               --}}
//{{-- These functions are to be called e.g.:                                                        --}}
//{{--    asynchCountWordsInXlsx(file, function(rc, msg, lang) {                                     --}}
//{{--      popovermsgbox_open('XLSX word count', `Total words: ${msg}`, 0, 'OK');                   --}}
//{{--      if (rc === 0) {                                                                          --}}
//{{--          totalWords = msg;                                                                    --}}
//{{--          langCode   = lang;                                                                   --}}
//{{--    });                                                                                        --}}
//{{--                                                                                               --}}
//{{-- Make sure to load the following constants before loading this script file                     --}}
//{{-- const wc_error_no_JSZIP     = "{{__('err_noJSZIP')}}";                                        --}}
//{{-- const wc_error_reading_file = "{{__('err_reading_file')}}";                                   --}}
//{{--                                                                                               --}}
//{{-- --------------------------------------------------------------------------------------------- --}}

//{{-- -------------------------------------------------------------- --}}
//{{-- Library includes                                               --}}
//{{-- -------------------------------------------------------------- --}}
import JSZip from 'jszip';

//{{-- -------------------------------------------------------------- --}}
//{{-- The word count functions                                       --}}
//{{-- -------------------------------------------------------------- --}}
window.asynchCountWordsInIdml = async function(file, callback) {
    let msg = null;
    let totalWords = 0;
    let xmlDocArray = [];
    console.log('Processing IDML file:', file.name);
    if (typeof JSZip === "undefined") {
        msg = wc_error_noJSZIP;
        callback(-2, msg, '');
    } else {
        JSZip.loadAsync(file).then(function(zip) {
            console.log('IDML zip contents:', Object.keys(zip.files));
            // Add promises for designmap.xml and metadata
            let designmapPromise = zip.file('designmap.xml') ? zip.file('designmap.xml').async('text') : Promise.resolve('');
            let metadataPromise = zip.file('META-INF/metadata.xml') ? zip.file('META-INF/metadata.xml').async('text') : Promise.resolve('');

            let storyPromises = Object.keys(zip.files).map(function(filename) {
                if (filename.startsWith('Stories/Story_') && filename.endsWith('.xml')) {
                    console.log('Processing story file:', filename);
                    return zip.file(filename).async('text').then(function(xmlText) {
                        let fileWords = 0;
                        let parser = new DOMParser();
                        let xmlDoc = parser.parseFromString(xmlText, "application/xml");
                        xmlDocArray.push(xmlDoc);
                        
                        // Get all text elements (Content tag in IDML)
                        let textNodes = xmlDoc.getElementsByTagName("Content");
                        console.log('Found text nodes:', textNodes.length);
                        Array.from(textNodes).forEach(function(node) {
                            const words = node.textContent.split(/\s+/).filter(Boolean);
                            console.log('Node text:', node.textContent, 'Words:', words.length);
                            fileWords += words.length;
                        });
                        
                        return fileWords;
                    });
                }
            }).filter(Boolean);

            // Include designmap.xml and metadata.xml in Promise.all
            Promise.all([designmapPromise, metadataPromise, ...storyPromises]).then(function([designmapXmlText, metadataXmlText, ...results]) {
                let parser = new DOMParser();
                let mainLangDescr = '';
                let mainLangCode = '';
                
                if (designmapXmlText) {
                    console.log('Found designmap.xml:', designmapXmlText);
                    let designmapDoc = parser.parseFromString(designmapXmlText, "application/xml");
                    xmlDocArray.push(designmapDoc);
                    
                    // Extract language from designmap.xml
                    const langNode = designmapDoc.querySelector('Language');
                    if (langNode) {
                        const primaryLang = langNode.getAttribute('PrimaryLanguageName');
                        if (primaryLang) {
                            mainLangCode = 'en-US'; // IDML files from InDesign use US English
                            mainLangDescr = getLocalizedLanguageDescription(mainLangCode);
                            console.log('Found language in designmap:', mainLangDescr);
                        }
                    }
                }
                if (metadataXmlText) {
                    console.log('Found metadata.xml:', metadataXmlText);
                    xmlDocArray.push(parser.parseFromString(metadataXmlText, "application/xml"));
                }
            
                totalWords = results.reduce((sum, count) => sum + count, 0);
                console.log('Total words:', totalWords);
                
                // Only use detectLanguageFromXml if we couldn't get language from designmap
                if (!mainLangDescr) {
                    [mainLangDescr, mainLangCode] = detectLanguageFromXml(xmlDocArray);
                }
                
                console.log('Detected language:', mainLangDescr, mainLangCode);
                callback(0, totalWords, mainLangDescr, mainLangCode);
            });
            
        }).catch(function(error) {
            console.error('Error processing IDML:', error);
            msg = wc_error_reading_file + ' ' + file.name;
            callback(-1, msg, '');
        });
    }
}

window.asynchCountWordsInPptx = async function(file, callback) {
    let msg = null;
    let totalWords = 0;
    let mainLanguage = null;
    let xmlDocArray = [];
    if (typeof JSZip === "undefined") {
        msg = wc_error_noJSZIP;
        callback(-2, msg, '');
    } else {
        JSZip.loadAsync(file).then(function(zip) {
            let slidePromises = Object.keys(zip.files).map(function(filename) {
                if (filename.startsWith('ppt/slides/slide') || 
                    filename.startsWith('ppt/notesSlides/notesSlide') || 
                    filename.startsWith('ppt/comments/comment') ||
                    filename.startsWith('ppt/comments/charts') ||
                    filename.startsWith('ppt/presentation') ||
                    filename.startsWith('ppt/docProps/core')
                ) {
                    return zip.file(filename).async('text').then(function(xmlText) {
                        let fileWords = 0;
                        let parser = new DOMParser();
                        let xmlDoc = parser.parseFromString(xmlText, "application/xml");
                        xmlDocArray.push(xmlDoc);
                        
                        // Get all text elements
                        let textNodes = xmlDoc.getElementsByTagNameNS("http://schemas.openxmlformats.org/drawingml/2006/main", "t");
        
                        Array.from(textNodes).forEach(function(node) {
                            fileWords += node.textContent.split(/\s+/).filter(Boolean).length;
                            //console.log(node.textContent.split(/\s+/).filter(Boolean).length + " == " + node.textContent);
                        });
                        
                        return fileWords;
                    });
                }
            }).filter(Boolean);
            
            Promise.all(slidePromises).then(results => {
                totalWords = results.reduce((sum, count) => sum + count, 0);
                const [mainLangDescr, mainLangCode] = detectLanguageFromXml(xmlDocArray); 
                callback(0, totalWords, mainLangDescr, mainLangCode);
            });
        }).catch(function(error) {
            msg = wc_error_reading_file + ' ' + file.name;
            callback(-1, msg, '');
        });
             
    }
}

window.asynchCountWordsInXlsx = async function(file, callback) {
    let msg = null;
    let totalWords = 0;
    let mainLanguage = null;
    let xmlDocArray = [];
    let parser = new DOMParser();
    let sharedStrings = [];
    if (typeof JSZip === "undefined") {
        msg = wc_error_noJSZIP;
        callback(-2, msg, '');
    } else {
        JSZip.loadAsync(file).then(function(zip) {
            // Initialize promises for core.xml, styles.xml, and theme1.xml
            let coreXmlPromise = zip.file('docProps/core.xml').async('text');
            let stylesFile = zip.file('xl/styles.xml');
            let stylesPromise = stylesFile ? stylesFile.async('text') : Promise.resolve('');
            let themeFile = zip.file('xl/theme/theme1.xml');
            let themePromise = themeFile ? themeFile.async('text') : Promise.resolve('');

            // Load shared strings if present
            let sharedStringsFile = zip.file('xl/sharedStrings.xml');
            let sharedStringsPromise = sharedStringsFile ? sharedStringsFile.async('text') : Promise.resolve('');

            // Adjust the Promise.all call to ensure shared strings are processed separately
            Promise.all([coreXmlPromise, stylesPromise, themePromise]).then(function([coreXmlText, stylesXmlText, themeXmlText]) {
                xmlDocArray.push(parser.parseFromString(coreXmlText, "application/xml"));
                xmlDocArray.push(parser.parseFromString(stylesXmlText, "application/xml"));
                xmlDocArray.push(parser.parseFromString(themeXmlText, "application/xml"));

                sharedStringsPromise.then(function(sharedStrXml) {
                    xmlDocArray.push(parser.parseFromString(sharedStrXml, "application/xml"));

                    if (sharedStrXml) {
                        let parser = new DOMParser();
                        let sharedStrDoc = parser.parseFromString(sharedStrXml, "application/xml");
                        let sst = sharedStrDoc.getElementsByTagNameNS("http://schemas.openxmlformats.org/spreadsheetml/2006/main", "si");
                        Array.from(sst).forEach(function(si) {
                            let text = Array.from(si.getElementsByTagNameNS("http://schemas.openxmlformats.org/spreadsheetml/2006/main", "t"))
                                .map(t => t.textContent).join("");
                            sharedStrings.push(text);
                        });
                    }

                    processWorksheets();
                });
            });
        
            function processWorksheets() {
                let sheetPromises = Object.keys(zip.files).map(function(filename) {
                    if (filename.startsWith('xl/worksheets/sheet') && filename.endsWith('.xml')) {
                        return zip.file(filename).async('text').then(function(xmlText) {
                            let fileWords = 0;
                            let xmlDoc = parser.parseFromString(xmlText, "application/xml");
                            xmlDocArray.push(xmlDoc);
                            let cells = xmlDoc.getElementsByTagNameNS("http://schemas.openxmlformats.org/spreadsheetml/2006/main", "c");
                            Array.from(cells).forEach(function(cell) {
                                if (cell.getAttribute("t") === "s") { // Shared string
                                    let sharedStringIndex = parseInt(cell.getElementsByTagName("v")[0].textContent, 10);
                                    if (!isNaN(sharedStringIndex) && sharedStrings[sharedStringIndex]) {
                                        fileWords += sharedStrings[sharedStringIndex]
                                            .split(/\s+/)
                                            .filter(word => /[\w]/.test(word))
                                            .length;
                                    }
                                } else if (cell.getElementsByTagName("v").length > 0) { // Inline string or other type
                                    let cellText = cell.getElementsByTagName("v")[0].textContent;
                                    fileWords += cellText
                                        .split(/\s+/)
                                        .filter(word => /[\w]/.test(word))
                                        .length;
                                }
                            });
                            return fileWords;
                        });
                    }
                }).filter(Boolean);
        
                Promise.all(sheetPromises).then(async results => {
                    totalWords = results.reduce((sum, count) => sum + count, 0);
                    const [xmlLangDescr, xmlLangCode] = detectLanguageFromXml(xmlDocArray);
                    const [contentLangDescr, contentLangCode] = await detectLanguageFromContent(sharedStrings);

                    const mainLangCode = xmlLangCode || contentLangCode;
                    const mainLangDescr = xmlLangDescr || contentLangDescr;

                    callback(0, totalWords, mainLangDescr, mainLangCode);
                });

            }
        }).catch(function(error) {
            msg = wc_error_reading_file + ' ' + file.name;
            callback(-1, msg, '');
        });
        
    }
}

window.asynchCountWordsInDocx = async function(file, callback) {
    let msg = null;
    let totalWords = 0;
    let mainLanguage = null;
    let xmlDocArray = [];
    if (typeof JSZip === "undefined") {
        msg = wc_error_noJSZIP;
        callback(-2, msg, '');
    } else {
        JSZip.loadAsync(file).then(function(zip) {
            // Add promises for styles.xml and settings.xml
            let stylesPromise = zip.file('word/styles.xml') ? zip.file('word/styles.xml').async('text') : Promise.resolve('');
            let settingsPromise = zip.file('word/settings.xml') ? zip.file('word/settings.xml').async('text') : Promise.resolve('');

            let docPromises = Object.keys(zip.files).map(function(filename) {
                if (filename.startsWith('word/') && filename.endsWith('.xml')) {
                    return zip.file(filename).async('text').then(function(xmlText) {
                        let fileWords = 0;
                        let parser = new DOMParser();
                        xmlDocArray.push(parser.parseFromString(xmlText, "application/xml"));
                        let xmlDoc = parser.parseFromString(xmlText, "application/xml");
                        let textNodes = xmlDoc.getElementsByTagNameNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "t");
                        Array.from(textNodes).forEach(function(node) {
                            fileWords += node.textContent.split(/\s+/).filter(Boolean).length;
                        });
                        return fileWords;
                    });
                }
            }).filter(Boolean);

            // Include styles.xml and settings.xml in Promise.all
            Promise.all([stylesPromise, settingsPromise, ...docPromises]).then(function([stylesXmlText, settingsXmlText, ...results]) {
                let parser = new DOMParser();
                xmlDocArray.push(parser.parseFromString(stylesXmlText, "application/xml"));
                xmlDocArray.push(parser.parseFromString(settingsXmlText, "application/xml"));
            
                let totalWords = results.reduce((sum, count) => sum + count, 0);
                const [mainLangDescr, mainLangCode] = detectLanguageFromXml(xmlDocArray); 
                callback(0, totalWords, mainLangDescr, mainLangCode);
            });
            
        }).catch(function(error) {
            msg = wc_error_reading_file + ' ' + file.name;
            callback(-1, msg, '');
        });
    }
}

window.asynchCountWordsInZip = async function (file, callback) {
    let totalWords = 0;
    let mainLangDescr = "";
    let mainLangCode = "";
    callback(0, totalWords, mainLangDescr, mainLangCode);
}

window.asynchCountWordsInOther = async function (file, callback) {
    let totalWords = 0;
    let mainLangDescr = "";
    let mainLangCode = "";
    callback(0, totalWords, mainLangDescr, mainLangCode);
}

//{{-- ---------------------------------------------------------------------------------- --}}
//{{-- Detect language using Google's CLD3 library (loaded in app.js) - used for XLX only --}}
//{{-- ---------------------------------------------------------------------------------- --}}
async function initializeCLD() {
    const { loadModule } = await import('cld3-asm');
    const cldModule = await loadModule();  // Load the module
    const detector = cldModule.create();   // Create the detector instance
    return detector;
}
function getDefaultRegionForLang(language) {
    const locale = new Intl.Locale(language);
    const maximized = locale.maximize();
    const { language: lang, region } = maximized;
    return region ? `${lang}-${region}` : lang;
}
window.detectLanguageFromContent = async function (stringsArray) {
    const cld = await initializeCLD();
    let cleanedText = stringsArray
        .flat().join(' ').toLowerCase().split(/\s+/)
        .filter(word =>word.length >= 5 && /^[a-zA-Z]+$/.test(word))
        .join(' ');

    let result = cld.findLanguage(cleanedText);
    let language = result.is_reliable ? result.language : '';

    let langCode = language ? getDefaultRegionForLang(language) : '';
    let langDescr = langCode ? getLocalizedLanguageDescription(langCode) : '';

    return [langDescr, langCode];
};

//{{-- ---------------------------------------------------------------------------------- --}}
//{{-- Detect language from the document's XML tags                                       --}}
//{{-- ---------------------------------------------------------------------------------- --}}
window.detectLanguageFromXml = function(xmlDocs) {
    let langTags = [];
    let mostFrequentLang = '';

    function findLangAttributes(element) {
        const langAttr = 'lang';
        const langPrefix = 'dc';
        const langSuffixes = ['lang', 'Lang'];
        const valSuffix = ':val';
    
        // Check if the element has attributes
        if (element.hasAttributes()) {
            // Check for general 'lang' attributes
            if (element.hasAttribute(langAttr)) {
                langTags.push(element.getAttribute(langAttr));
            } else if (element.hasAttributeNS(null, langAttr)) {
                langTags.push(element.getAttributeNS(null, langAttr));
            } else if (element.localName === 'language' && element.prefix === langPrefix && element.textContent.trim()) {
                // Special handling for <dc:language> element
                langTags.push(element.textContent.trim());
            } else {
                // Check for 'lang' attributes in other elements
                Array.from(element.attributes).forEach(attr => {
                    const isLangAttr = attr.name.endsWith(valSuffix) && langSuffixes.some(suffix => element.localName.endsWith(suffix));
                    const isThemeFontLang = element.localName.endsWith('themeFontLang') && attr.name.endsWith(valSuffix);
                    
                    if ((isLangAttr || isThemeFontLang) && attr.value.trim()) {
                        langTags.push(attr.value);
                    }
                });
            }
        }
    
        // Recursive search on children
        Array.from(element.children).forEach(findLangAttributes);
    }   

    try {
        xmlDocs.forEach(xmlDoc => {
            findLangAttributes(xmlDoc.documentElement);
        });
    } catch (error) {
        console.error('Error processing XML documents:', error);
    }

    // Return most frequent language or empty string
    if (langTags.length > 0) {
        let frequency = langTags.reduce((acc, lang) => {
            acc[lang] = (acc[lang] || 0) + 1;
            return acc;
        }, {});

        mostFrequentLang = Object.keys(frequency).reduce((a, b) => {
            return frequency[a] > frequency[b] ? a : b;
        }, Object.keys(frequency)[0]);
    }

    let langCode = normalizeLanguageTag(mostFrequentLang);
    let langDescr = (langCode !== '') ? getLocalizedLanguageDescription(langCode) : '';

    return [langDescr, langCode];
};

//{{-- Function to normalize the language tag to the standard format (e.g. en-US) by extracting the language and region components --}}
window.normalizeLanguageTag = function(languageTag) {
    let code = '';
    if (isThere(languageTag)) {
        try {
            let sanitizedTag = languageTag.replace(/_/g, '-');
            sanitizedTag = sanitizedTag.split('-').slice(0, 2).join('-');

            // Create a Locale object
            let locale = new Intl.Locale(sanitizedTag);

            // Extract the language and region, ensuring they are in the correct format
            let language = locale.language;
            let region = locale.region || '';

            // Return in the standard "language-COUNTRY" format
            if (language && region) 
                code = `${language}-${region}`;
        } catch (error) {
            console.log('normalizeLanguageTag error: ' + error);
        }
    }
    return code;
}

//{{-- Get a localized (to glb_locale) description of a language code --}}
window.getLocalizedLanguageDescription = function(langCode) {
    // Make sure we have the right locale
    const locale = glb_locale.split('-')[0];

    // Create an instance of Intl.DisplayNames
    const displayNames = new Intl.DisplayNames([locale], { type: 'language' });

    // Extract the language and region from the langCode
    const [language, region] = langCode.split('-');
    const abbreviatedRegion = region.toUpperCase();

    // Get the localized language name
    const localizedLanguage = displayNames.of(language);
    const capitalizedLanguage = localizedLanguage.charAt(0).toUpperCase() + localizedLanguage.slice(1);

    // Optionally, you can also get the localized region name if needed
    const displayNamesRegion = new Intl.DisplayNames([locale], { type: 'region' });
    const localizedRegion = displayNamesRegion.of(region);

    return `${capitalizedLanguage} (${abbreviatedRegion})`;
}

window.asynchGetMMMinutes = function (file, callback) {
    function getFileDurationInMinutes(file) {
        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const element = file.type.startsWith('video') ? document.createElement('video') : document.createElement('audio');

            element.addEventListener('loadedmetadata', () => {
                URL.revokeObjectURL(url);
                const durationInMinutes = element.duration / 60;
                resolve(durationInMinutes);
            });

            element.addEventListener('error', (err) => {
                URL.revokeObjectURL(url);
                reject(err);
            });

            element.src = url;
        });
    }

    getFileDurationInMinutes(file)
        .then(duration => {
            callback(0, parseFloat(duration.toFixed(2)), '', '');
        })
        .catch(function (error) {
            const msg = 'Error reading file: ' + file.name;
            callback(-1, msg, '');
        });
}
